import type {
  DocumentSources,
  DocumentTemplate,
  DocumentTemplateVersion,
  LayoutCheck,
  Rendition,
  RenditionHistory,
  RenditionMissing,
  RenditionTemplateOption,
  RenditionsAvailable,
  TemplateLayout,
} from "@wms/domain";
import { usePortalStore } from "../portal/store";
import { API_BASE, ApiError, api, problemOf, refreshOnce } from "./client";
import type { Problem } from "./client";
import { downloadHeaders } from "./download";
import { portalApi } from "./portal";

/**
 * KZ-DOC 30.10.2026 (ТЗ шаблонов, разделы 11–15): every document opens in one viewer — the preview, «Скачать PDF» and
 * «Печать» take the same stored file (one rendition id, one SHA-256). The staff ARM and the client cabinet use the
 * same calls under their own prefixes and tokens.
 */
export type Channel = "staff" | "portal";

/** The kind of document behind a card of the generic catalogue (plan «Контракты между пакетами»). */
export const ENTITY_KINDS: Record<string, string> = {
  expected_receipts: "expected_receipt",
  shipment_orders: "shipment_order",
  stock_counts: "stock_count",
  task_lists: "task_list",
  cargo_units: "cargo_unit",
  operation_documents: "operation_document",
  invoices: "invoice",
  acts: "act",
  service_registers: "service_register",
};

/** Cards whose own print action opens the viewer, without a separate «Документы и печать» button (a pallet is a place). */
const PRINT_ONLY_KINDS: Record<string, string> = { locations: "pallet" };

export function kindOfEntity(entity: string, printOnly = false): string | null {
  return ENTITY_KINDS[entity] ?? (printOnly ? PRINT_ONLY_KINDS[entity] ?? null : null);
}

/** ТЗ §2 / AT02: label forms keep their own print path (thermal and A4 labels); the document viewer does not replace them. */
export const LABEL_FORMS = new Set(["cargo_label", "pallet_label", "cell_labels"]);

/** Whether an old print button of this card opens the document viewer (else the old HTML form, as before). */
export function printOpensViewer(entity: string, actionCode: string): boolean {
  return !!kindOfEntity(entity, true) && !LABEL_FORMS.has(legacyFormOf(actionCode));
}

/** «print-forms/torg12» → «torg12», «billing/register-print» → «register-print»: the code of the old form. */
export function legacyFormOf(actionCode: string): string {
  const code = actionCode.replace(/^\/+/, "");
  if (code.startsWith("print-forms/")) return code.slice("print-forms/".length);
  const parts = code.split("/");
  return parts[parts.length - 1] ?? code;
}

export function matchesLegacy(option: Pick<RenditionTemplateOption, "legacy_form">, legacyForm?: string | null): boolean {
  return !!legacyForm && !!option.legacy_form && option.legacy_form === legacyForm;
}

/**
 * The layout the viewer opens with: the one an old print button names (`legacy_form`); else the first that can be
 * formed now (a version valid on the document date, nothing missing); else the first with a version; else the first at
 * all — its card then says why there is nothing to form. The others stay one click away in the list.
 */
export function pickTemplate(options: RenditionTemplateOption[], legacyForm?: string | null): RenditionTemplateOption | null {
  if (!options.length) return null;
  const legacy = options.find((o) => matchesLegacy(o, legacyForm));
  if (legacy) return legacy;
  return options.find((o) => o.version !== null && !o.missing.length) ?? options.find((o) => o.version !== null) ?? options[0];
}

/** TMP12: one key per intent to form a file; a repeat after an unknown result sends the same key. */
export function newRequestKey(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `rk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Report parameters travel as one JSON query value; an empty set is no value at all. */
export function paramsQuery(params?: Record<string, unknown> | null): Record<string, unknown> | undefined {
  if (!params || !Object.keys(params).length) return undefined;
  return { params: JSON.stringify(params) };
}

/** The state of the viewer that a refused or failed call means (TMP19). Never «сервер недоступен» for a subject error. */
export type ViewerProblemKind = "offline" | "forbidden" | "not_found" | "not_connected" | "no_template" | "missing" | "invalid" | "error";

export interface ViewerProblem {
  kind: ViewerProblemKind;
  title: string;
  detail: string;
  /** The request id of a failure (AT25): the person reads it out to the support. */
  reference?: string;
  missing?: RenditionMissing[];
}

type RenditionProblem = Problem & { reference?: string; missing?: RenditionMissing[] };

export function viewerProblem(error: unknown, online = true): ViewerProblem {
  if (!(error instanceof ApiError)) {
    const detail = error instanceof Error ? error.message : "Неизвестная ошибка";
    return { kind: "error", title: "Не удалось открыть документ", detail };
  }
  const p = error.problem as RenditionProblem;
  const detail = p.detail ?? p.title;
  if (p.status === 0 || p.code === "network") {
    return {
      kind: "offline",
      title: online ? "Нет связи с сервером" : "Нет сети",
      detail: online ? "Сервер не ответил: проверьте сеть и повторите." : "Устройство не в сети: подключитесь и повторите.",
    };
  }
  if (p.code === "missing_requisites" || (p.status === 422 && Array.isArray(p.missing))) {
    return { kind: "missing", title: "Не хватает данных для выбранного макета", detail, missing: p.missing ?? [] };
  }
  if (p.code === "no_template") return { kind: "no_template", title: "Нет применимого макета", detail };
  if (p.status === 403) {
    // a report PDF of a user bound to warehouses needs one chosen warehouse — a choice, not a closed door
    const choose = /Выберите склад/.test(detail);
    return { kind: "forbidden", title: choose ? "Выберите склад" : "Нет доступа к документу", detail };
  }
  if (p.code === "stale_client") {
    // a route the server does not have yet (a kind not connected to the viewer) — not «обновите вкладку» alone
    return {
      kind: "not_connected",
      title: "Просмотр этого документа ещё не подключён",
      detail: "Сервер пока не формирует PDF для этого документа. Обновите страницу; если не поможет — откройте прежнюю печатную форму.",
    };
  }
  if (p.status === 404) {
    const kindMissing = /^Вид документа/.test(p.detail ?? "");
    return kindMissing
      ? { kind: "not_connected", title: "Для этого документа ещё нет макетов", detail: "Вид документа ещё не подключён к единому просмотру. Откройте прежнюю печатную форму или обратитесь к администратору." }
      : { kind: "not_found", title: "Документ не найден", detail };
  }
  if (p.status === 422) return { kind: "invalid", title: "Проверьте параметры", detail };
  return {
    kind: "error",
    title: p.status >= 500 ? "Не удалось сформировать PDF" : p.title || "Не удалось выполнить действие",
    detail,
    reference: p.reference,
  };
}

/**
 * KZ-DOC D3: the parameters of a report PDF. The stored PDF of a report is shared through its history, so a user bound
 * to warehouses prints one warehouse: the only one is filled in, several need a choice in the report form (the server
 * refuses «все мои склады» with 403 «Выберите склад…»).
 */
export function reportPdfParams(
  params: Record<string, unknown> | null,
  boundWarehouses: string[],
  hasWarehouseParam: boolean,
): { params: Record<string, unknown> | null; hint: string | null } {
  if (params === null) return { params: null, hint: null };
  if (params.warehouse_id || !boundWarehouses.length) return { params, hint: null };
  if (boundWarehouses.length === 1) return { params: { ...params, warehouse_id: boundWarehouses[0] }, hint: null };
  return {
    params: null,
    hint: hasWarehouseParam
      ? "Для «Просмотр и печать» выберите склад в параметрах отчёта и нажмите «Сформировать»: PDF формируется по одному складу."
      : "PDF этого отчёта формируется по одному складу, а выбрать склад здесь нельзя: обратитесь к администратору.",
  };
}

/** A PDF answer: the bytes and what the server says about them (`X-Rendition-Id`, `X-Content-SHA256`). */
export interface PdfFile {
  blob: Blob;
  sha256: string | null;
  renditionId: string | null;
  filename: string | null;
}

export function filenameOf(disposition: string | null): string | null {
  if (!disposition) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      /* a broken encoding falls back to the plain name */
    }
  }
  const plain = /filename="([^"]+)"/i.exec(disposition);
  return plain ? plain[1] : null;
}

/** SHA-256 of the bytes as lowercase hex — the same as `pdf_sha256` of the rendition. */
export async function sha256Hex(blob: Blob): Promise<string | null> {
  const subtle = (globalThis.crypto as Crypto | undefined)?.subtle;
  if (!subtle) return null; // a plain-http page has no WebCrypto: the check is skipped, never invented
  const digest = await subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function portalHeaders(): Record<string, string> {
  const token = usePortalStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** GET of a binary answer with the token of the channel (and `X-Company-Id` for the staff), one refresh on 401. */
export async function fetchPdf(channel: Channel, path: string, retry = true): Promise<PdfFile> {
  const url = channel === "portal" ? portalApi.printUrl(path) : API_BASE + path;
  const headers = channel === "portal" ? portalHeaders() : downloadHeaders();
  let response: Response;
  try {
    response = await fetch(url, { headers });
  } catch {
    throw new ApiError({ title: "Нет связи с сервером", status: 0, detail: "Нет связи с сервером: проверьте сеть и повторите", code: "network" });
  }
  if (response.status === 401 && retry) {
    const refreshed = channel === "portal" ? await usePortalStore.getState().refresh().catch(() => false) : await refreshOnce().catch(() => false);
    if (refreshed) return fetchPdf(channel, path, false);
  }
  if (!response.ok) {
    const text = await response.text();
    let data: Partial<Problem> | null = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      /* not json */
    }
    throw new ApiError(problemOf(response, data));
  }
  return {
    blob: await response.blob(),
    sha256: response.headers.get("X-Content-SHA256"),
    renditionId: response.headers.get("X-Rendition-Id"),
    filename: filenameOf(response.headers.get("Content-Disposition")),
  };
}

/** Saves bytes the page already holds under the name the server gave. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function documentPath(kind: string, documentId: string): string {
  return `/documents/${encodeURIComponent(kind)}/${encodeURIComponent(documentId)}/renditions`;
}

export interface CreateRenditionBody {
  template_id?: string | null;
  params?: Record<string, unknown>;
  request_key?: string;
}

export const renditionsApi = {
  available(channel: Channel, kind: string, documentId: string, params?: Record<string, unknown> | null) {
    const path = `${documentPath(kind, documentId)}/available`;
    return channel === "portal" ? portalApi.get<RenditionsAvailable>(path, paramsQuery(params)) : api.get<RenditionsAvailable>(path, paramsQuery(params));
  },
  create(channel: Channel, kind: string, documentId: string, body: CreateRenditionBody) {
    const path = documentPath(kind, documentId);
    return channel === "portal" ? portalApi.post<Rendition>(path, body) : api.post<Rendition>(path, body);
  },
  /** UI06 — the staff only: the cabinet has no history of other people's actions. */
  history(kind: string, documentId: string, params?: Record<string, unknown> | null) {
    return api.get<RenditionHistory>(documentPath(kind, documentId), paramsQuery(params));
  },
  get(id: string) {
    return api.get<Rendition>(`/document-renditions/${id}`);
  },
  /** The stored file: `download=false` for the preview and the print, `true` for «Скачать PDF» (recorded). */
  pdf(channel: Channel, id: string, download: boolean) {
    return fetchPdf(channel, `/document-renditions/${id}/pdf?download=${download ? "true" : "false"}`);
  },
  /** TMP23: «Открыт диалог печати» — not «напечатано». */
  printDialog(channel: Channel, id: string) {
    const path = `/document-renditions/${id}/print-dialog`;
    return channel === "portal" ? portalApi.post<{ status: string; label: string }>(path, {}) : api.post<{ status: string; label: string }>(path, {});
  },
};

// ---- the catalogue «Шаблоны документов» --------------------------------------------------------------------------
export interface TemplateFilters {
  doc_kind?: string;
  company_id?: string;
  purpose?: string;
  status?: string;
  q?: string;
}

export interface TemplateCreateBody {
  name: string;
  doc_kind?: string | null;
  purpose?: string | null;
  company_id?: string | null;
  basis?: string | null;
  base_template_id?: string | null;
}

export interface VersionPatchBody {
  layout?: TemplateLayout;
  valid_from?: string | null;
  valid_to?: string | null;
  note?: string | null;
}

export const templatesApi = {
  sources: () => api.get<DocumentSources>("/document-sources"),
  list: (filters: TemplateFilters = {}) => api.get<{ items: DocumentTemplate[]; total: number }>("/document-templates", { ...filters }),
  get: (id: string) => api.get<DocumentTemplate>(`/document-templates/${id}`),
  create: (body: TemplateCreateBody) => api.post<DocumentTemplate>("/document-templates", body),
  patch: (id: string, body: { name?: string; basis?: string | null; is_active?: boolean; sort_order?: number }) =>
    api.patch<DocumentTemplate>(`/document-templates/${id}`, body),
  newVersion: (id: string, fromVersionId?: string | null) =>
    api.post<DocumentTemplateVersion>(`/document-templates/${id}/versions`, { from_version_id: fromVersionId ?? null }),
  patchVersion: (id: string, body: VersionPatchBody) => api.patch<DocumentTemplateVersion>(`/document-template-versions/${id}`, body),
  validate: (id: string) => api.post<LayoutCheck>(`/document-template-versions/${id}/validate`, {}),
  submit: (id: string) => api.post<DocumentTemplateVersion>(`/document-template-versions/${id}/submit`, {}),
  publish: (id: string, body: { reason: string; valid_from?: string | null }) =>
    api.post<DocumentTemplateVersion>(`/document-template-versions/${id}/publish`, body),
  archive: (id: string, body: { reason: string }) => api.post<DocumentTemplateVersion>(`/document-template-versions/${id}/archive`, body),
  accountingReview: (id: string, body: { reason?: string | null }) =>
    api.post<DocumentTemplateVersion>(`/document-template-versions/${id}/accounting-review`, body),
  /** «Предпросмотр» on the training data of the kind (or a real document by its id), watermarked by the server. */
  preview: (id: string, documentId?: string | null) =>
    fetchPdf("staff", `/document-template-versions/${id}/preview.pdf${documentId ? `?document_id=${encodeURIComponent(documentId)}` : ""}`),
};

/** Words of the catalogue: the statuses of a version and the accountant's check (the server sends the same lists). */
export const VERSION_STATUS_LABELS: Record<string, string> = {
  draft: "Черновик",
  review: "На проверке",
  published: "Опубликован",
  archived: "В архиве",
};

export const VERSION_STATUS_COLORS: Record<string, string> = { draft: "gold", review: "blue", published: "green", archived: "default" };

export const ACCOUNTING_REVIEW_LABELS: Record<string, string> = {
  not_required: "Сверка не требуется",
  pending: "Не сверено бухгалтером",
  confirmed: "Сверено бухгалтером",
};

export const PURPOSE_LABELS: Record<string, string> = {
  operational: "Операционный",
  accounting: "Учётный",
  financial: "Финансовый",
  international: "Международный",
  report: "Отчёт",
};

/** «1 страница», «2 страницы», «5 страниц» — the count said in Russian. */
export function pagesWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} страница`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} страницы`;
  return `${n} страниц`;
}

/** «1,2 МБ», «84 КБ». */
export function sizeWord(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} МБ`;
}
